import React, { useState, useMemo, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Search, Check, Filter, ChevronDown, ChevronRight } from "lucide-react";
import server from "../utils/serverConfig";
import toast from "react-hot-toast";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";
import WebSocketManager from "../contexts/websocketManager";
import { useGlobalData } from "../hooks/useGlobalHooks";

const BulkOrderPlacementModal = ({ isOpen, onClose, users, indexMultipliers, onSuccess, existingOrders = [] }) => {
    const { userMappings, multiAssetUnderlyings } = useGlobalData();
    const [formData, setFormData] = useState({
        symbol: "",
        strategy: "",
        baseQty: "",
        side: "1", // 1 for Buy, 2 for Sell (standard broker convention usually, but we'll use 1/-1 if needed)
        orderType: "MARKET",
        limitPrice: "0",
        stopPrice: "0",
        validity: "5",
        entry: "0",
        referenceIndex: "NIFTY",
        calculationMode: "MULTIPLIER", // MULTIPLIER or EXACT
        exactQty: "",
        strike_label: "",
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [brokerFilter, setBrokerFilter] = useState("ALL");
    const [isTemplatesExpanded, setIsTemplatesExpanded] = useState(false);
    
    // LTP and Subscription state
    const currentSymbol = formData.symbol?.trim()?.toUpperCase();
    const ltp = useLtpStore((state) => state.ltps[currentSymbol]);
    const [subscribing, setSubscribing] = useState(false);

    const handleSubscribe = async () => {
        if (!currentSymbol) return;
        setSubscribing(true);
        try {
            await WebSocketManager.getInstance().subscribeToSymbols([currentSymbol]);
            toast.success(`Subscribed to ${currentSymbol}`);
        } catch (err) {
            toast.error("Subscription failed");
        } finally {
            setSubscribing(false);
        }
    };

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedClients(new Set());
        }
    }, [isOpen]);

    const eligibleClients = useMemo(() => {
        if (!users || !indexMultipliers) return [];

        // Deduplicate users by clientId + broker
        const uniqueUsersMap = new Map();
        users.forEach(u => {
            if (u.status !== "active") return;
            const cid = u.clientId || u.client_id;
            const broker = u.broker || "fyers";
            const key = `${cid}_${broker}`;
            if (!uniqueUsersMap.has(key)) {
                uniqueUsersMap.set(key, u);
            }
        });
        const uniqueUsers = Array.from(uniqueUsersMap.values());

        const validMaUnderlyings = (multiAssetUnderlyings || ["NIFTY", "BANKNIFTY"]).map(u => u.toUpperCase());
        const getClientMultiplier = (u) => {
            const cid = (u.clientId || u.client_id)?.toString().trim();
            const productVal = (u.product || "").toLowerCase();
            const mapping = userMappings?.find(m => (m.client_id || m.clientId)?.toString().trim() === cid);
            const multiAsset = Number(
                u.multi_asset_multiplier ??
                u.multiasset_multiplier ??
                mapping?.multi_asset_multiplier ??
                mapping?.multiasset_multiplier ??
                (indexMultipliers?.find(m => (m.client_id || m.clientId)?.toString().trim() === cid && (m.is_multi_asset || m.isMultiAsset))?.multiplier) ??
                0
            );
            const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
            const effectiveMaMul = multiAsset > 0 ? multiAsset : (Number(u.multiplier || mapping?.multiplier) || 1);
            const strike = (formData.strike_label || "").trim().toLowerCase();
            const isStrike2OrAbove = strike.startsWith("strike_") && strike !== "strike_1";

            if (isMultiAsset) {
                if (isStrike2OrAbove) {
                    return 0; // Strike 2 and Strike 3 NEVER apply in Multi-Asset mode
                }
                if (validMaUnderlyings.includes(formData.referenceIndex.toUpperCase())) {
                    return effectiveMaMul;
                }
                return 0;
            }

            const targetIndexName = isStrike2OrAbove
                ? `${formData.referenceIndex.toUpperCase()}_${strike.toUpperCase()}`
                : formData.referenceIndex.toUpperCase();

            const found = indexMultipliers?.find(m =>
                (m.client_id || m.clientId)?.toString().trim() === cid &&
                (m.broker || "").toLowerCase() === (u.broker || "fyers").toLowerCase() &&
                String(m.index).toUpperCase() === targetIndexName
            );
            if (found?.multiplier) return Number(found.multiplier);

            if (!isStrike2OrAbove && !isMultiAsset) {
                const baseFound = indexMultipliers?.find(m =>
                    (m.client_id || m.clientId)?.toString().trim() === cid &&
                    (m.broker || "").toLowerCase() === (u.broker || "fyers").toLowerCase() &&
                    String(m.index).toUpperCase() === formData.referenceIndex.toUpperCase()
                );
                return baseFound?.multiplier ? Number(baseFound.multiplier) : 0;
            }
            return 0;
        };

        return uniqueUsers.filter(u => {
            if (formData.calculationMode === "MULTIPLIER") {
                const multiplier = getClientMultiplier(u);
                return multiplier !== undefined && multiplier !== null && Number(multiplier) > 0;
            }
            return true;
        }).map(u => {
            const cid = (u.clientId || u.client_id)?.toString().trim();
            const productVal = (u.product || "").toLowerCase();
            const mapping = userMappings?.find(m => (m.client_id || m.clientId)?.toString().trim() === cid);
            const multiAssetMul = Number(
                u.multi_asset_multiplier ??
                u.multiasset_multiplier ??
                mapping?.multi_asset_multiplier ??
                mapping?.multiasset_multiplier ??
                (indexMultipliers?.find(m => (m.client_id || m.clientId)?.toString().trim() === cid && (m.is_multi_asset || m.isMultiAsset))?.multiplier) ??
                0
            );
            const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
            const multiplier = getClientMultiplier(u);
            return { 
                ...u, 
                currentMultiplier: multiplier || 0,
                multi_asset_multiplier: multiAssetMul,
                isMultiAsset,
                product: isMultiAsset ? 'multi_asset' : 'optimus'
            };
        });
    }, [users, indexMultipliers, formData.referenceIndex, formData.strike_label, formData.calculationMode, multiAssetUnderlyings, userMappings]);

    const filteredClients = useMemo(() => {
        let list = eligibleClients;
        if (brokerFilter !== "ALL") {
            list = list.filter(c => (c.broker || "").toLowerCase() === brokerFilter.toLowerCase());
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(c =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.clientId || c.client_id || "").toLowerCase().includes(q) ||
                (c.label || "").toLowerCase().includes(q) ||
                (c.broker || "").toLowerCase().includes(q) ||
                (c.isMultiAsset && (q === "ma" || "multiasset".includes(q) || "multi-asset".includes(q)))
            );
        }
        return list;
    }, [eligibleClients, brokerFilter, searchQuery]);

    const uniqueLabels = useMemo(() => {
        const labels = new Set();
        eligibleClients.forEach(c => {
            if (c.label) labels.add(c.label);
        });
        return Array.from(labels).sort();
    }, [eligibleClients]);

    const formatOrderTime = (dt) => {
        if (!dt || dt === "N/A") return "-";
        try {
            const date = new Date(dt);
            return date.toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            });
        } catch (e) { return dt; }
    };

    const uniqueTemplates = useMemo(() => {
        if (!existingOrders || !Array.isArray(existingOrders)) return [];

        const templates = [];
        const seen = new Set();

        existingOrders.forEach(order => {
            // Only templates for Buy orders as per user request
            if (String(order.side) !== "1") return;

            // Only allow specific indices
            const allowedSymbols = ["BSE:SENSEX", "NSE:NIFTY", "NSE:BANKNIFTY", "NSE:MIDCPNIFTY", "NSE:FINNIFTY"];
            const isAllowedSymbol = allowedSymbols.some(prefix => order.symbol && order.symbol.startsWith(prefix));

            if (!isAllowedSymbol) return;

            const time = order.orderdatetime || order.orderDateTime || "";
            // Unique by symbol, strategy, orderType, limitPrice, stopPrice
            const key = `${order.symbol}-${order.strategy}-${order.orderType}-${order.side}-${order.limitPrice || order.limit_price || 0}-${order.stopPrice || order.stop_price || 0}`;

            if (!seen.has(key)) {
                seen.add(key);
                templates.push({
                    symbol: order.symbol,
                    strategy: order.strategy,
                    orderType: order.orderType,
                    side: String(order.side),
                    limitPrice: String(order.limitPrice || order.limit_price || 0),
                    stopPrice: String(order.stopPrice || order.stop_price || 0),
                    time: time // keeping the most recent time for display or sorting
                });
            }
        });

        // Sort by time descending (newest first)
        return templates.sort((a, b) => new Date(b.time) - new Date(a.time));
    }, [existingOrders]);

    const getClientUniqueId = (c) => `${c.clientId || c.client_id}_${c.broker || "fyers"}`;

    const toggleSelectAll = () => {
        if (selectedClients.size === filteredClients.length) {
            setSelectedClients(new Set());
        } else {
            setSelectedClients(new Set(filteredClients.map(c => getClientUniqueId(c))));
        }
    };

    const toggleClientSelection = (uniqueId) => {
        const next = new Set(selectedClients);
        if (next.has(uniqueId)) next.delete(uniqueId);
        else next.add(uniqueId);
        setSelectedClients(next);
    };

    const toggleLabelSelection = (label) => {
        const labelClients = filteredClients.filter(c => c.label === label);
        if (labelClients.length === 0) return;

        const labelIds = labelClients.map(c => getClientUniqueId(c));
        const allSelected = labelIds.every(id => selectedClients.has(id));

        const next = new Set(selectedClients);
        if (allSelected) {
            labelIds.forEach(id => next.delete(id));
        } else {
            labelIds.forEach(id => next.add(id));
        }
        setSelectedClients(next);
    };

    const toggleMultiAssetSelection = () => {
        const maClients = filteredClients.filter(c => c.isMultiAsset);
        if (maClients.length === 0) return;

        const maIds = maClients.map(c => getClientUniqueId(c));
        const allSelected = maIds.every(id => selectedClients.has(id));

        const next = new Set(selectedClients);
        if (allSelected) {
            maIds.forEach(id => next.delete(id));
        } else {
            maIds.forEach(id => next.add(id));
        }
        setSelectedClients(next);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            
            // Reset prices when switching order types
            if (name === "orderType") {
                if (value === "LIMIT") {
                    next.stopPrice = "0";
                } else if (value === "MARKET") {
                    next.limitPrice = "0";
                    next.stopPrice = "0";
                } else if (value === "STOP-MARKET") {
                    next.limitPrice = "0";
                }
            }
            
            return next;
        });
    };

    const applyTemplate = (template) => {
        let newOrderType = template.orderType;
        const lPrice = Number(template.limitPrice);
        const sPrice = Number(template.stopPrice);

        if (lPrice > 0 && (!sPrice || sPrice === 0)) {
            newOrderType = "LIMIT";
        } else if (lPrice > 0 && sPrice > 0) {
            newOrderType = "STOP-LIMIT";
        }

        setFormData(prev => ({
            ...prev,
            symbol: template.symbol,
            strategy: template.strategy,
            orderType: newOrderType,
            side: template.side,
            limitPrice: template.limitPrice,
            stopPrice: template.stopPrice
        }));
        toast.success("Template applied");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedClients.size === 0) {
            toast.error("Please select at least one client");
            return;
        }
        if (!formData.symbol || !formData.strategy) {
            toast.error("Please fill required fields");
            return;
        }

        if (formData.calculationMode === "MULTIPLIER" && !formData.baseQty) {
            toast.error("Please specify Base Quantity");
            return;
        }

        if (formData.calculationMode === "EXACT" && !formData.exactQty) {
            toast.error("Please specify Exact Quantity");
            return;
        }

        setSubmitting(true);
        try {
            const orderPayload = Array.from(selectedClients).map(uniqueId => {
                const client = eligibleClients.find(c => `${c.clientId || c.client_id}_${c.broker || "fyers"}` === uniqueId);
                const clientId = client.clientId || client.client_id;

                let qty;
                if (formData.calculationMode === "MULTIPLIER") {
                    qty = Number(formData.baseQty) * (client?.currentMultiplier || 0);
                } else {
                    qty = Number(formData.exactQty);
                }

                return {
                    client_id: clientId,
                    symbol: formData.symbol,
                    strategy: formData.strategy,
                    qty: qty,
                    side: Number(formData.side),
                    orderType: formData.orderType,
                    limitPrice: Number(formData.limitPrice),
                    stopPrice: Number(formData.stopPrice),
                    validity: Number(formData.validity) || 5,
                    entry: Number(formData.entry) || 0,
                    strike_label: formData.strike_label || "",
                    broker: client?.broker || "fyers"
                };
            });

            const dhanXtsPayload = orderPayload.filter(o => o.broker === "dhan_xts" || o.broker === "dhan_ss");
            const dhanXtsAnuragPayload = orderPayload.filter(o => o.broker === "dhan_xts_anurag" || o.broker === "dhan_ss_anurag");
            const otherBrokersPayload = orderPayload.filter(o => !["dhan_xts", "dhan_ss", "dhan_xts_anurag", "dhan_ss_anurag"].includes(o.broker));

            // console.log("🚀 Placing bulk orders:");
            // console.log("- Other brokers:", otherBrokersPayload);
            // console.log("- Dhan XTS:", dhanXtsPayload);
            // console.log("- Dhan XTS Anurag:", dhanXtsAnuragPayload);

            // Handler to flatten API response
            const flattenResponse = (apiData) => {
                if (!apiData || !Array.isArray(apiData)) return [];
                const flattened = [];
                apiData.forEach(clientEntry => {
                    if (clientEntry.order_resp && Array.isArray(clientEntry.order_resp)) {
                        clientEntry.order_resp.forEach(order => {
                            flattened.push({
                                clientId: clientEntry.client_id,
                                symbol: order.symbol,
                                qty: order.qty,
                                status: order.status === "failed" ? "Rejected" : (order.status || "Pending"),
                                rejectReason: order.response?.message || order.response?.error || order.message || "",
                                orderId: order.response?.id || order.id || "N/A",
                                broker: eligibleClients.find(c => (c.clientId || c.client_id) === clientEntry.client_id)?.broker || "fyers"
                            });
                        });
                    }
                });
                return flattened;
            };

            let finalOrders = [];
            const apiCalls = [];

            if (otherBrokersPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.godseye.baseUrl}order-placement`, otherBrokersPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "Bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            if (dhanXtsPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.ss_dhan.baseUrl}order-placement-xts`, dhanXtsPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "XTS bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            if (dhanXtsAnuragPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.ss_dhan_anurag.baseUrl}order-placement-xts`, dhanXtsAnuragPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "XTS Anurag bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            try {
                const results = await Promise.all(apiCalls);
                for (const result of results) {
                    finalOrders = [...finalOrders, ...result];
                }
                toast.success(`Bulk orders submitted`);
            } catch (err) {
                throw err;
            }

            if (finalOrders.length > 0) {
                onSuccess(finalOrders);
                onClose();
            }
        } catch (err) {
            console.error("❌ Bulk order placement failed:", err);
            toast.error("Bulk order placement failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm p-4">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-xl font-bold">Order Placement</h2>
                        <p className="text-gray-400 text-xs mt-1">Place manual orders for multiple clients at once</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6 flex flex-col lg:flex-row gap-6">

                    {/* Form Side */}
                    <div className="lg:w-1/2 space-y-4 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto pr-2 sleek-scrollbar space-y-4">

                            {uniqueTemplates.length > 0 && (
                                <div>
                                    <div
                                        className="flex items-center justify-between cursor-pointer mb-2 group py-1.5 px-2 -mx-2 rounded hover:bg-[#14161f] transition-colors"
                                        onClick={() => setIsTemplatesExpanded(!isTemplatesExpanded)}
                                    >
                                        <h3 className="text-blue-400 font-semibold text-[10px] uppercase tracking-wider flex items-center gap-2 group-hover:text-blue-300 transition-colors">
                                            <Filter className="w-3 h-3" /> Quick Templates (Existing Buy Orders)
                                        </h3>
                                        {isTemplatesExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-blue-400 opacity-60 group-hover:opacity-100 transition-opacity" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-blue-400 opacity-60 group-hover:opacity-100 transition-opacity" />
                                        )}
                                    </div>

                                    {isTemplatesExpanded && (
                                        <div className="flex flex-wrap gap-2 mb-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                            {uniqueTemplates.map((t, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => applyTemplate(t)}
                                                    className="bg-[#0f1116] border border-gray-800 hover:border-blue-500 hover:bg-blue-500/5 px-2.5 py-2 rounded-lg text-left transition-all group flex flex-col gap-0.5"
                                                >
                                                    <div className="flex items-center justify-between gap-4">
                                                        <span className="text-xs font-bold text-gray-300 group-hover:text-blue-400 truncate max-w-[140px]">{t.symbol}</span>
                                                        <span className="text-[10px] text-gray-500 font-mono bg-gray-900/50 px-1 rounded">{formatOrderTime(t.time)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[11px] text-blue-400/70 font-medium truncate max-w-[100px]">{t.strategy}</span>
                                                        <span className="text-[10px] text-gray-600 uppercase pt-0.5">{t.orderType}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 mt-0.5">
                                                        <span className="text-[11px] text-gray-500 font-mono">L: {t.limitPrice}</span>
                                                        <span className="text-[11px] text-gray-500 font-mono ml-1">S: {t.stopPrice}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="h-px bg-gray-800 w-full my-4"></div>
                                </div>
                            )}

                            <h3 className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4 border-b border-blue-900/30 pb-2">Order Configuration</h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <div className="flex justify-between items-end mb-1.5">
                                        <label className="block text-gray-400 text-xs font-medium uppercase">Symbol <span className="text-red-500">*</span></label>
                                        <div className="flex items-center gap-2">
                                            {currentSymbol && (
                                                <>
                                                    {ltp !== undefined ? (
                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded">
                                                            <span className="text-[10px] text-blue-400 font-bold uppercase">LTP:</span>
                                                            <span className="text-xs text-white font-mono font-bold">{ltp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSubscribe}
                                                            disabled={subscribing}
                                                            className="text-[10px] bg-orange-500/10 border border-orange-500/30 text-orange-400 font-bold px-2 py-0.5 rounded hover:bg-orange-500/20 transition-all flex items-center gap-1"
                                                        >
                                                            {subscribing ? <div className="w-2 h-2 border border-orange-400/30 border-t-orange-400 rounded-full animate-spin"></div> : "Subscribe"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <input
                                        type="text" name="symbol" value={formData.symbol} onChange={handleInputChange}
                                        placeholder="e.g. NSE:NIFTY26MAR25500CE"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600 font-mono text-sm"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase tracking-wider">Calculation Mode</label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, calculationMode: "MULTIPLIER" }))}
                                            className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold border transition-all ${formData.calculationMode === "MULTIPLIER" ? "bg-blue-600 border-blue-500 text-white" : "bg-transparent border-gray-700 text-gray-500"}`}
                                        >Multiplier Based</button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, calculationMode: "EXACT" }))}
                                            className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold border transition-all ${formData.calculationMode === "EXACT" ? "bg-orange-600 border-orange-500 text-white" : "bg-transparent border-gray-700 text-gray-500"}`}
                                        >Exact Quantity</button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Strategy <span className="text-red-500">*</span></label>
                                    <input
                                        type="text" name="strategy" value={formData.strategy} onChange={handleInputChange}
                                        placeholder="e.g. PDI_MA_PC5"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Strike Label</label>
                                    <div className="flex gap-1 mb-2">
                                        {["strike_1", "strike_2", "strike_3"].map(label => (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, strike_label: label }))}
                                                className={`flex-1 py-1 rounded text-[10px] font-bold border transition-all ${formData.strike_label === label ? "bg-blue-600 border-blue-500 text-white" : "bg-[#0f1116] border-gray-700 text-gray-400 hover:border-gray-500"}`}
                                            >
                                                {label.replace('_', ' ')}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, strike_label: "" }))}
                                            className={`px-2 py-1 rounded text-[10px] font-bold border transition-all ${!["strike_1", "strike_2", "strike_3"].includes(formData.strike_label) && formData.strike_label ? "bg-blue-600 border-blue-500 text-white" : "bg-[#0f1116] border-gray-700 text-gray-400"}`}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <input
                                        type="text" name="strike_label" value={formData.strike_label || ""} onChange={handleInputChange}
                                        placeholder="Custom label..."
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600 text-sm"
                                    />
                                </div>

                                {formData.calculationMode === "MULTIPLIER" ? (
                                    <>
                                        <div>
                                            <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Multiplier Index <span className="text-red-500">*</span></label>
                                            <select
                                                name="referenceIndex" value={formData.referenceIndex} onChange={handleInputChange}
                                                className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none appearance-none cursor-pointer"
                                            >
                                                {["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX"].map(idx => (
                                                    <option key={idx} value={idx}>{idx}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Base Qty (1x) <span className="text-red-500">*</span></label>
                                            <input
                                                type="number" name="baseQty" value={formData.baseQty} onChange={handleInputChange}
                                                className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-span-1">
                                        <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Exact Quantity <span className="text-red-500">*</span></label>
                                        <input
                                            type="number" name="exactQty" value={formData.exactQty} onChange={handleInputChange}
                                            placeholder="e.g. 50"
                                            className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Side <span className="text-red-500">*</span></label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, side: "1" }))}
                                            className={`flex-1 py-2 rounded-lg font-bold border transition-all ${formData.side === "1" ? "bg-emerald-600 border-emerald-500 text-white" : "bg-transparent border-gray-700 text-gray-400"}`}
                                        >Buy</button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, side: "-1" }))}
                                            className={`flex-1 py-2 rounded-lg font-bold border transition-all ${formData.side === "-1" ? "bg-red-600 border-red-500 text-white" : "bg-transparent border-gray-700 text-gray-400"}`}
                                        >Sell</button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Order Type</label>
                                    <select
                                        name="orderType" value={formData.orderType} onChange={handleInputChange}
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none cursor-pointer"
                                    >
                                        <option value="MARKET">MARKET</option>
                                        <option value="LIMIT">LIMIT</option>
                                        <option value="STOP-LIMIT">STOP-LIMIT</option>
                                        <option value="STOP-MARKET">STOP-MARKET</option>
                                    </select>
                                </div>

                                {(formData.orderType === "LIMIT" || formData.orderType === "STOP-LIMIT") && (
                                    <div>
                                        <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Limit Price</label>
                                        <input
                                            type="number" step="0.05" name="limitPrice" value={formData.limitPrice} onChange={handleInputChange}
                                            className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all"
                                        />
                                    </div>
                                )}

                                {(formData.orderType === "STOP-LIMIT" || formData.orderType === "STOP-MARKET") && (
                                    <div>
                                        <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Stop Price</label>
                                        <input
                                            type="number" step="0.05" name="stopPrice" value={formData.stopPrice} onChange={handleInputChange}
                                            className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Validity</label>
                                    <input
                                        type="number" name="validity" value={formData.validity} onChange={handleInputChange}
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Entry</label>
                                    <input
                                        type="number" name="entry" value={formData.entry} onChange={handleInputChange}
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Client Selection Side */}
                    <div className="lg:w-1/2 flex flex-col h-full overflow-hidden">
                        <h3 className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4 border-b border-blue-900/30 pb-2">Client Selection ({selectedClients.size} selected)</h3>

                        {/* Filters */}
                        <div className="flex gap-2 mb-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                    type="text" placeholder="Search client..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#0f1116] border border-gray-700 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white"
                                />
                            </div>
                            <select
                                value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)}
                                className="bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none cursor-pointer"
                            >
                                <option value="ALL">All Brokers</option>
                                <option value="FYERS">Fyers</option>
                                <option value="DHAN_XTS">Dhan XTS</option>
                                <option value="DHAN_HQ">Dhan HQ</option>
                                <option value="DHAN_XTS_ANURAG">Dhan XTS Anurag</option>
                                <option value="NUBRA">Nubra</option>
                            </select>
                        </div>

                        {/* Labels & Tags row */}
                        {(() => {
                            const maClients = filteredClients.filter(c => c.isMultiAsset);
                            const maSelectedCount = maClients.filter(c => selectedClients.has(`${c.clientId || c.client_id}_${c.broker || "fyers"}`)).length;
                            const isAllMaSelected = maSelectedCount === maClients.length && maClients.length > 0;

                            if (uniqueLabels.length === 0 && maClients.length === 0) return null;

                            return (
                                <div className="flex flex-wrap gap-2 mb-4 px-1">
                                    {maClients.length > 0 && (
                                        <button
                                            key="M-A"
                                            type="button"
                                            onClick={toggleMultiAssetSelection}
                                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all border flex items-center gap-1.5 ${isAllMaSelected
                                                ? 'bg-purple-600 border-purple-500 text-white shadow-sm'
                                                : maSelectedCount > 0
                                                    ? 'bg-purple-900/40 border-purple-700 text-purple-200'
                                                    : 'bg-[#14161f] border-purple-800/50 text-purple-400 hover:border-purple-500'
                                                }`}
                                            title="Toggle select all Multi-Asset clients"
                                        >
                                            M-A
                                            <span className={`px-1 rounded ${isAllMaSelected ? 'bg-purple-800' : 'bg-gray-800 text-purple-300'}`}>
                                                {maSelectedCount}/{maClients.length}
                                            </span>
                                        </button>
                                    )}

                                    {uniqueLabels.map(label => {
                                        const labelClients = filteredClients.filter(c => c.label === label);
                                        if (labelClients.length === 0) return null;

                                        const selectedCount = labelClients.filter(c => selectedClients.has(`${c.clientId || c.client_id}_${c.broker || "fyers"}`)).length;
                                        const isAllSelected = selectedCount === labelClients.length && labelClients.length > 0;

                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => toggleLabelSelection(label)}
                                                className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all border flex items-center gap-1.5 ${isAllSelected
                                                    ? 'bg-blue-600 border-blue-500 text-white'
                                                    : selectedCount > 0
                                                        ? 'bg-blue-900/40 border-blue-700 text-blue-200'
                                                        : 'bg-[#14161f] border-gray-700 text-gray-500 hover:border-gray-500'
                                                    }`}
                                            >
                                                {label}
                                                <span className={`px-1 rounded ${isAllSelected ? 'bg-blue-800' : 'bg-gray-800 text-gray-400'}`}>
                                                    {selectedCount}/{labelClients.length}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {/* List */}
                        <div className="flex-1 bg-[#0f1116] rounded-xl border border-gray-800 overflow-hidden flex flex-col">
                            <div className="bg-[#14161f] px-4 py-2 flex items-center justify-between border-b border-gray-800">
                                <button onClick={toggleSelectAll} className="text-[10px] text-blue-400 font-bold hover:text-blue-300 uppercase">
                                    {selectedClients.size === filteredClients.length ? "Deselect All" : "Select All Visible"}
                                </button>
                                <span className="text-[10px] text-gray-500 uppercase">{filteredClients.length} clients eligible</span>
                            </div>
                            <div className="flex-1 overflow-y-auto sleek-scrollbar">
                                {filteredClients.map((c, idx) => {
                                    const clientId = c.clientId || c.client_id;
                                    const uniqueId = `${clientId}_${c.broker || "fyers"}`;
                                    const isSelected = selectedClients.has(uniqueId);
                                    return (
                                        <div
                                            key={uniqueId}
                                            onClick={() => toggleClientSelection(uniqueId)}
                                            className={`px-4 py-2 border-b border-gray-800/50 flex items-center gap-3 cursor-pointer hover:bg-blue-500/5 group transition-colors ${isSelected ? 'bg-blue-500/10' : ''}`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-500' : 'border-gray-600 group-hover:border-gray-400'}`}>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={4} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <p className="text-white text-sm font-medium">{c.name}</p>
                                                    {c.isMultiAsset && (
                                                        <span
                                                            title={`Multi-Asset multiplier active: ${c.multi_asset_multiplier || c.currentMultiplier}`}
                                                            className="px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded bg-purple-500/20 text-purple-300 border border-purple-500/40 shrink-0"
                                                        >
                                                            MA
                                                        </span>
                                                    )}
                                                    {c.label && (
                                                        <span className="text-[10px] px-1 rounded font-bold border border-blue-500/30 bg-blue-500/10 text-blue-400 shrink-0">
                                                            {c.label}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-400 text-[12px] font-mono">{clientId} | {c.broker?.toUpperCase()}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-gray-400 text-[10px] uppercase font-bold">
                                                    {formData.calculationMode === "MULTIPLIER" ? "Multiplier" : "Mode"}
                                                </p>
                                                <p className={`${formData.calculationMode === "MULTIPLIER" ? "text-blue-400" : "text-gray-500"} text-sm font-bold`}>
                                                    {formData.calculationMode === "MULTIPLIER" ? `${c.currentMultiplier}x` : "Exact"}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredClients.length === 0 && (
                                    <div className="p-8 text-center">
                                        <Filter className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                                        <p className="text-gray-500 text-sm">No eligible clients found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 bg-[#14161f]">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-lg text-gray-300 hover:bg-gray-800 font-medium transition-colors"
                    >Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || selectedClients.size === 0}
                        className="px-8 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold transition-all shadow-lg shadow-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {submitting ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : null}
                        Place Orders ({selectedClients.size})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkOrderPlacementModal;
